import { Link } from 'react-router-dom';
import { ArrowRight, Stethoscope, HeartPulse, Shield, Clock, Microscope, UserCheck, Activity, Phone, Calendar, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const services = [
  {
    title: 'General Consultations',
    description: 'Comprehensive evaluations with experienced physicians to address your health concerns.',
    icon: Stethoscope,
    featured: true,
  },
  {
    title: 'Health Assessments',
    description: 'Thorough preventive screenings and wellness check-ups tailored to your health profile.',
    icon: Activity,
  },
  {
    title: 'Diagnosis & Treatment',
    description: 'Accurate diagnosis using advanced techniques, with effective treatment plans.',
    icon: Microscope,
  },
  {
    title: 'Preventive Care',
    description: 'Proactive healthcare to prevent illness and maintain long-term wellness.',
    icon: Shield,
  },
  {
    title: 'Minor Surgical Procedures',
    description: 'Safe, in-office procedures performed by skilled surgeons with minimal downtime.',
    icon: HeartPulse,
  },
  {
    title: 'Wound & Postoperative Care',
    description: 'Expert wound management and recovery support to ensure optimal healing.',
    icon: UserCheck,
  },
];

const stats = [
  { value: '25K+', label: 'Patients Served', icon: Users },
  { value: '15+', label: 'Years of Care', icon: Clock },
  { value: '98%', label: 'Patient Satisfaction', icon: HeartPulse },
  { value: '20+', label: 'Specialist Doctors', icon: Award },
];

const whyChoose = [
  {
    title: 'Board-Certified Physicians',
    desc: 'Our team of experienced doctors and surgeons brings decades of combined expertise.',
    icon: Award,
  },
  {
    title: 'Compassionate Care',
    desc: 'We treat every patient with dignity, empathy, and personalized attention.',
    icon: HeartPulse,
  },
  {
    title: 'Advanced Technology',
    desc: 'State-of-the-art medical equipment for accurate diagnosis and effective treatment.',
    icon: Microscope,
  },
  {
    title: 'Patient Education',
    desc: 'We empower you with knowledge to make informed decisions about your health.',
    icon: UserCheck,
  },
];

const testimonials = [
  {
    quote: "Dr. Morgan and her team provided exceptional care during my procedure. The entire staff made me feel comfortable and well-informed every step of the way.",
    author: 'Sarah M.',
    role: 'Patient',
  },
  {
    quote: "The level of attention and personalized care I received was outstanding. They truly listen and take time to explain everything.",
    author: 'Michael T.',
    role: 'Patient',
  },
  {
    quote: "From my first consultation to follow-up care, the experience was seamless. I highly recommend HealthCheck Medical.",
    author: 'Jennifer W.',
    role: 'Patient',
  },
];

export const Home = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[92vh] flex items-center bg-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=1920&q=80" 
            alt="Medical team providing patient care"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/30"></div>
        </div>
        
        {/* Decorative blobs */}
        <div className="absolute top-32 right-20 w-72 h-72 bg-emerald/20 rounded-full blur-3xl z-0"></div>
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-coral/15 rounded-full blur-3xl z-0"></div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald/10 text-emerald font-semibold text-xs uppercase tracking-wider mb-6 rounded-full border border-emerald/20">
              <HeartPulse className="w-4 h-4" />
              Comprehensive Medical Care
            </span>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl lg:text-6xl text-navy leading-tight mb-6">
              Expert Medical & Surgical Care, <span className="stat-gradient">Tailored to You</span>
            </h1>
            <p className="text-lg text-navy-600 leading-relaxed mb-8 max-w-xl">
              At HealthCheck Medical, our experienced doctors and surgeons deliver safe, professional, and compassionate healthcare—focused on your wellness, recovery, and quality of life.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                className="bg-emerald hover:bg-emerald-600 text-white rounded-lg font-semibold tracking-wide uppercase text-xs h-12 px-8 shadow-lg hover:shadow-xl hover:shadow-emerald/40 transition-all"
                data-testid="hero-cta-primary"
              >
                <Link to="/contact">
                  <Calendar className="mr-2 w-4 h-4" />
                  Book Appointment
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-2 border-navy text-navy hover:bg-navy hover:text-white rounded-lg font-semibold tracking-wide uppercase text-xs h-12 px-8 bg-transparent"
                data-testid="hero-cta-secondary"
              >
                <a href="tel:+18558311992">
                  <Phone className="mr-2 w-4 h-4" />
                  Call (855) 831-1992
                </a>
              </Button>
            </div>

            {/* Quick info bar */}
            <div className="mt-12 flex flex-wrap gap-6 text-sm text-navy-600">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald pulse-emerald"></div>
                <span>Mon–Fri: 9AM–5PM</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-coral"></div>
                <span>Sat–Sun: 11AM–4PM</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-sunny"></div>
                <span>Accepting New Patients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="gradient-vibrant py-16 relative overflow-hidden" data-testid="stats-section">
        <div className="absolute inset-0 gradient-mesh opacity-50"></div>
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.label}
                className="text-center animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur flex items-center justify-center mx-auto mb-3 border border-white/20">
                  <stat.icon className="w-7 h-7 text-emerald-100" />
                </div>
                <div className="stat-value-white text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-sm text-emerald-100 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-navy-50" data-testid="services-section">
        <div className="container-custom">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              Our Services
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy mb-4">
              Comprehensive Medical & Surgical Services
            </h2>
            <p className="text-navy-600 leading-relaxed">
              From routine check-ups to minor surgical procedures and postoperative care, we provide complete healthcare under one roof.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card 
                key={service.title}
                className="bg-white border-silver-light shadow-card card-hover group"
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-sm flex items-center justify-center mb-4 ${
                    service.featured ? 'bg-emerald' : 'bg-navy-50 group-hover:bg-emerald'
                  } transition-colors`}>
                    <service.icon className={`w-6 h-6 ${
                      service.featured ? 'text-white' : 'text-navy group-hover:text-white'
                    } transition-colors`} />
                  </div>
                  <h3 className="font-manrope font-semibold text-navy text-lg mb-2">{service.title}</h3>
                  <p className="text-sm text-navy-600 mb-4">{service.description}</p>
                  <Link 
                    to="/services"
                    className="inline-flex items-center text-emerald font-semibold text-sm group-hover:gap-2 transition-all"
                    data-testid={`service-link-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-600 text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
              data-testid="view-all-services-btn"
            >
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white" data-testid="why-us-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
                Why Choose Us
              </span>
              <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy mb-6">
                Trusted Healthcare, Delivered with Compassion
              </h2>
              <p className="text-navy-600 leading-relaxed mb-8">
                At HealthCheck Medical, we believe quality healthcare combines medical excellence with genuine human connection. Our team is committed to improving your health, restoring wellness, and enhancing your quality of life.
              </p>
              
              <div className="space-y-6">
                {whyChoose.map((item) => (
                  <div key={item.title} className="flex gap-4" data-testid={`why-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="w-10 h-10 rounded-sm bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-emerald" />
                    </div>
                    <div>
                      <h4 className="font-manrope font-semibold text-navy mb-1">{item.title}</h4>
                      <p className="text-sm text-navy-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&q=80"
                alt="Doctor consulting with patient"
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-8 -left-8 bg-gradient-to-br from-coral to-sunny p-8 rounded-2xl shadow-coral hidden md:block floating-badge">
                <HeartPulse className="w-10 h-10 text-white mb-3" />
                <div className="stat-value-white text-3xl font-bold text-white mb-1">98%</div>
                <div className="text-white/90 text-sm">Patient Satisfaction</div>
              </div>
              <div className="absolute -top-6 -right-6 bg-emerald p-5 rounded-2xl shadow-float hidden md:block">
                <Award className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-navy-50" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              Patient Stories
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy">
              What Our Patients Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index}
                className="bg-white border-silver-light shadow-card"
                data-testid={`testimonial-${index}`}
              >
                <CardContent className="p-8">
                  <div className="text-emerald text-4xl font-serif mb-4 leading-none">"</div>
                  <p className="text-navy-700 leading-relaxed mb-6">{testimonial.quote}</p>
                  <div>
                    <div className="font-manrope font-semibold text-navy">{testimonial.author}</div>
                    <div className="text-sm text-navy-500">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 gradient-vibrant"></div>
        <div className="absolute inset-0 gradient-mesh"></div>
        <div className="absolute top-10 right-10 w-64 h-64 bg-coral/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-sunny/20 rounded-full blur-3xl"></div>
        
        <div className="container-custom relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
            <HeartPulse className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-manrope font-bold text-3xl md:text-5xl text-white mb-4">
            Your Health, Our Priority
          </h2>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
            Schedule an appointment today and experience healthcare that puts you first.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              asChild
              className="bg-white text-emerald-600 hover:bg-coral hover:text-white rounded-lg font-semibold tracking-wide uppercase text-xs h-12 px-8 shadow-xl transition-all"
              data-testid="cta-schedule-btn"
            >
              <Link to="/contact">
                <Calendar className="mr-2 w-4 h-4" />
                Book Appointment
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-emerald rounded-lg font-semibold tracking-wide uppercase text-xs h-12 px-8 bg-transparent"
              data-testid="cta-call-btn"
            >
              <a href="tel:+18558311992">
                <Phone className="mr-2 w-4 h-4" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
